import type { ApiErrorBody } from './types';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export class NetworkError extends Error {
  constructor(message = 'Нет соединения с сервером') {
    super(message);
    this.name = 'NetworkError';
  }
}

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

export function isNetworkError(error: unknown): boolean {
  return error instanceof NetworkError || (error instanceof TypeError && !(error instanceof ApiError));
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const body = value as Partial<ApiErrorBody>;
  return typeof body.error === 'string' && typeof body.message === 'string';
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Не вызывать глобальный logout при 401 (нужно для login/register). */
  skipUnauthorizedRedirect?: boolean;
}

export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, signal, skipUnauthorizedRedirect = false } = options;

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      credentials: 'include',
      signal,
      headers: {
        Accept: 'application/json',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    throw new NetworkError();
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const raw: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const message = isApiErrorBody(raw) ? raw.message : 'Ошибка запроса к серверу';
    const code = isApiErrorBody(raw) ? raw.error : 'http_error';

    if (response.status === 401 && !skipUnauthorizedRedirect) {
      unauthorizedHandler?.();
    }

    throw new ApiError(response.status, code, message);
  }

  return raw as T;
}
